// @ts-nocheck
import { useState } from 'react'
import { Download, Upload, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

export function CustomerImportExport() {
  const api = useApi()
  const [importResults, setImportResults] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)

  // CSVエクスポート
  const handleExport = async () => {
    try {
      setIsExporting(true)
      setError(null)

      // fetchWithAuthを使ってレスポンス全体を取得
      const response = await api.fetchWithAuth('/api/customers/export')

      if (!response.ok) {
        throw new Error('エクスポートに失敗しました')
      }

      // CSVファイルをダウンロード
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      setError(err.message)
    } finally {
      setIsExporting(false)
    }
  }

  // ファイル選択
  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedFile(file)
      setImportResults(null)
      setError(null)
    }
  }

  // インポートプレビュー
  const handleImportPreview = async () => {
    if (!selectedFile) {
      setError('ファイルを選択してください')
      return
    }

    try {
      setIsImporting(true)
      setError(null)

      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await api.fetchWithAuth('/api/customers/import?dryRun=true', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'プレビューに失敗しました')
      }

      const results = await response.json()
      setImportResults(results)
    } catch (err) {
      console.error('Preview error:', err)
      setError(err.message)
    } finally {
      setIsImporting(false)
    }
  }

  // 実際のインポート
  const handleImportConfirm = async () => {
    if (!selectedFile) {
      setError('ファイルを選択してください')
      return
    }

    try {
      setIsImporting(true)
      setError(null)

      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await api.fetchWithAuth('/api/customers/import?dryRun=false', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'インポートに失敗しました')
      }

      const results = await response.json()
      setImportResults(results)

      // 成功時はファイル選択をリセット
      if (results.imported > 0) {
        setSelectedFile(null)
        // ファイル入力をリセット
        const fileInput = document.getElementById('csv-file-input')
        if (fileInput) fileInput.value = ''
      }
    } catch (err) {
      console.error('Import error:', err)
      setError(err.message)
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* エクスポートセクション */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Download className="mr-2" size={20} />
          顧客データをエクスポート
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          全ての顧客データをCSVファイルとしてダウンロードします。
        </p>
        <button
          onClick={handleExport}
          disabled={isExporting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
        >
          {isExporting ? (
            <>
              <Loader className="animate-spin mr-2" size={16} />
              エクスポート中...
            </>
          ) : (
            <>
              <Download className="mr-2" size={16} />
              CSVエクスポート
            </>
          )}
        </button>
      </div>

      {/* インポートセクション */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Upload className="mr-2" size={20} />
          顧客データをインポート
        </h3>
        
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            CSVファイルから顧客データを一括登録します。
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800 mb-4">
            <strong>注意:</strong> 重複する電話番号のデータはスキップされます。
          </div>
        </div>

        {/* ファイル選択 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CSVファイルを選択
          </label>
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {/* アクションボタン */}
        <div className="flex space-x-2">
          <button
            onClick={handleImportPreview}
            disabled={!selectedFile || isImporting}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            {isImporting ? (
              <>
                <Loader className="animate-spin mr-2" size={16} />
                処理中...
              </>
            ) : (
              <>
                <AlertCircle className="mr-2" size={16} />
                プレビュー
              </>
            )}
          </button>

          {importResults && importResults.valid.length > 0 && !importResults.imported && (
            <button
              onClick={handleImportConfirm}
              disabled={isImporting}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {isImporting ? (
                <>
                  <Loader className="animate-spin mr-2" size={16} />
                  インポート中...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={16} />
                  インポート実行
                </>
              )}
            </button>
          )}
        </div>

        {/* エラー表示 */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
            <XCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {/* インポート結果 */}
        {importResults && (
          <div className="mt-6 space-y-4">
            {/* サマリー */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-600">総件数</div>
                <div className="text-2xl font-bold">{importResults.total}</div>
              </div>
              <div className="bg-green-50 p-3 rounded-md">
                <div className="text-sm text-green-600">有効</div>
                <div className="text-2xl font-bold text-green-700">{importResults.valid.length}</div>
              </div>
              <div className="bg-red-50 p-3 rounded-md">
                <div className="text-sm text-red-600">エラー</div>
                <div className="text-2xl font-bold text-red-700">{importResults.invalid.length}</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-md">
                <div className="text-sm text-yellow-600">重複</div>
                <div className="text-2xl font-bold text-yellow-700">{importResults.duplicates.length}</div>
              </div>
            </div>

            {/* インポート完了メッセージ */}
            {importResults.imported > 0 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="font-semibold text-green-800">インポート完了</div>
                  <div className="text-sm text-green-700">
                    {importResults.imported}件の顧客データをインポートしました。
                  </div>
                </div>
              </div>
            )}

            {/* エラー詳細 */}
            {importResults.invalid.length > 0 && (
              <div className="border border-red-200 rounded-md">
                <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                  <h4 className="font-semibold text-red-800 flex items-center">
                    <XCircle className="mr-2" size={16} />
                    エラー詳細 ({importResults.invalid.length}件)
                  </h4>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {importResults.invalid.map((item, i) => (
                    <div key={i} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                      <div className="text-sm font-medium text-gray-900">
                        行 {item.row}
                      </div>
                      <div className="text-sm text-red-600 mt-1">
                        {item.errors.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 重複詳細 */}
            {importResults.duplicates.length > 0 && (
              <div className="border border-yellow-200 rounded-md">
                <div className="bg-yellow-50 px-4 py-2 border-b border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 flex items-center">
                    <AlertCircle className="mr-2" size={16} />
                    重複データ ({importResults.duplicates.length}件)
                  </h4>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {importResults.duplicates.map((item, i) => (
                    <div key={i} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                      <div className="text-sm font-medium text-gray-900">
                        行 {item.row}: {item.record['電話番号']}
                      </div>
                      <div className="text-sm text-yellow-700 mt-1">
                        既存の顧客: {item.existing.name || '（名前未設定）'} ({formatPhoneNumberForDisplay(item.existing.phoneNumber)})
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSVフォーマット説明 */}
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-3">CSVフォーマット</h4>
        <div className="text-sm text-blue-800 space-y-2">
          <p>以下の列を含むCSVファイルをインポートできます：</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>電話番号</strong> (必須): E.164形式 (例: +81901234567)</li>
            <li><strong>名前</strong>: 顧客名</li>
            <li><strong>フリガナ</strong>: カタカナ</li>
            <li><strong>会社名</strong></li>
            <li><strong>部署</strong></li>
            <li><strong>役職</strong></li>
            <li><strong>メール</strong></li>
            <li><strong>顧客タイプ</strong>: individual または corporate</li>
            <li><strong>優先度</strong>: low, normal, high, vip</li>
            <li><strong>タグ</strong>: カンマ区切り (例: VIP,法人)</li>
            <li><strong>メモ</strong></li>
          </ul>
          <p className="mt-3">
            <strong>ヒント:</strong> 「CSVエクスポート」ボタンでダウンロードしたファイルがフォーマットの参考になります。
          </p>
        </div>
      </div>
    </div>
  )
}
